"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { useRouter } from "@/lib/navigation";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import type { MoveIndexWorkerProjectInput } from "./schema";

const MoveIndexWorkerProjectConfirmQuery = graphql(`
  query MoveIndexWorkerProjectConfirmQuery(
    $indexWorkerId: ID!
    $targetProjectId: ID!
  ) {
    indexWorker: node(id: $indexWorkerId) {
      __typename
      ... on IndexWorker {
        id
        name
      }
    }
    targetProject: node(id: $targetProjectId) {
      __typename
      ... on IndexWorkerProject {
        id
        name
      }
    }
  }
`);

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function MoveIndexWorkerProjectConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const router = useRouter();

  const isTarget =
    isToolUIPart(part) && getToolName(part) === "move_index_worker_project";
  const isInputAvailable = part.state === "input-available";

  const input = (part.input ?? {}) as Partial<MoveIndexWorkerProjectInput>;
  const indexWorkerId = input.indexWorkerId ?? "";
  const targetProjectId = input.targetProjectId ?? "";

  const [{ data, fetching }] = useQuery({
    query: MoveIndexWorkerProjectConfirmQuery,
    variables: {
      indexWorkerId,
      targetProjectId,
    },
    pause: !(isTarget && isInputAvailable && indexWorkerId && targetProjectId),
  });

  if (!isTarget || !isInputAvailable) {
    return null;
  }

  const indexWorkerNode =
    data?.indexWorker?.__typename === "IndexWorker" ? data.indexWorker : null;
  const targetProjectNode =
    data?.targetProject?.__typename === "IndexWorkerProject"
      ? data.targetProject
      : null;
  const indexWorkerName = indexWorkerNode?.name ?? null;
  const targetProjectName = targetProjectNode?.name ?? null;
  const canMove = Boolean(indexWorkerNode && targetProjectNode);

  return (
    <div className="p-4 text-sm">
      <div className="mb-2 font-medium">
        <Trans>Move Index Worker</Trans>
      </div>

      <ul className="mb-4 list-disc space-y-1 pl-5">
        <li>
          <Trans>Index Worker</Trans>:{" "}
          {fetching ? (
            <Skeleton className="h-4 w-28 bg-gray-400 dark:bg-gray-600" />
          ) : (
            <span className="font-medium">
              {indexWorkerName || <Trans>Unknown</Trans>}
            </span>
          )}
        </li>
        <li>
          <Trans>Target Project</Trans>:{" "}
          {fetching ? (
            <Skeleton className="h-4 w-28 bg-gray-400 dark:bg-gray-600" />
          ) : (
            <span className="font-medium">
              {targetProjectName || <Trans>Unknown project</Trans>}
            </span>
          )}
        </li>
      </ul>

      <div className="flex gap-2">
        {canMove ? (
          <ApproveButton
            toolCallId={part.toolCallId}
            toolName={getToolName(part)}
            addToolResult={addToolResult}
            sendMessage={sendMessage}
            onSuccess={() => router.refresh()}
            overrideInput={{
              indexWorkerId: indexWorkerNode!.id,
              targetProjectId: targetProjectNode!.id,
            }}
          >
            <Trans>Move</Trans>
          </ApproveButton>
        ) : (
          <Button size="sm" disabled>
            <Trans>Move</Trans>
          </Button>
        )}
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
